/**
 * Filter Utilities
 *
 * Shared pre-filtering logic for consistent badge counts across all status bar filters.
 * Each filter should use this utility to filter units by ALL OTHER filters (excluding its own).
 */

import type { IndustryType } from '@/types';

// Minimal unit interface for filtering (works with Unit, UnitFilterData, etc.)
interface FilterableUnit {
  id: string;
  status: string;
  industry_type?: string;
  company_id?: string;
  primary_group_id?: string | null;
  unit_code: string;
}

export interface PreFilterOptions<T extends FilterableUnit> {
  /** Units to filter */
  units: T[];
  /** Selected industry types - pass undefined to skip this filter */
  selectedIndustryTypes?: IndustryType[];
  /** Selected company IDs - pass undefined to skip this filter */
  selectedCompanyIds?: string[];
  /** Selected group IDs - pass undefined to skip this filter */
  selectedGroupIds?: string[];
  /** Selected model codes (3-char prefix) - pass undefined to skip this filter */
  selectedModels?: string[];
  /** Selected statuses - pass undefined to skip this filter */
  selectedStatuses?: string[];
  /** Function to get live status for a unit (from UnitStatusContext) */
  getLiveStatus?: (unitId: string, fallbackStatus: string) => string;
}

/**
 * Pre-filters units by the specified criteria.
 *
 * Pass undefined for any filter you want to EXCLUDE (i.e., the filter the calling dropdown controls).
 *
 * @example
 * // For Industry filter badge counts:
 * preFilterUnits({
 *   units,
 *   selectedIndustryTypes: undefined,  // Don't filter by industry - that's what this dropdown controls
 *   selectedCompanyIds,              // DO filter by company
 *   selectedGroupIds,                // DO filter by group
 *   selectedModels,                  // DO filter by model
 *   selectedStatuses,                // DO filter by status
 *   getLiveStatus,
 * });
 */
export function preFilterUnits<T extends FilterableUnit>({
  units,
  selectedIndustryTypes,
  selectedCompanyIds,
  selectedGroupIds,
  selectedModels,
  selectedStatuses,
  getLiveStatus,
}: PreFilterOptions<T>): T[] {
  // If industry types is explicitly set to empty array, return nothing (user cleared all selections)
  if (selectedIndustryTypes !== undefined && selectedIndustryTypes.length === 0) {
    return [];
  }

  return units.filter(unit => {
    // Industry type filter (NO industry types selected = NO data shown)
    if (selectedIndustryTypes !== undefined) {
      if (selectedIndustryTypes.length === 0) return false;  // No industry types = no matches
      const unitType = (unit.industry_type || 'film') as IndustryType;
      if (!selectedIndustryTypes.includes(unitType)) return false;
    }

    // Company filter (NO companies selected = NO data shown)
    if (selectedCompanyIds !== undefined) {
      if (selectedCompanyIds.length === 0) return false;  // No companies = no matches
      const matchesCompany =
        (unit.company_id && selectedCompanyIds.includes(unit.company_id)) ||
        (selectedCompanyIds.includes('__unassigned__') && !unit.company_id);
      if (!matchesCompany) return false;
    }

    // Group filter (NO groups selected = NO data shown)
    if (selectedGroupIds !== undefined) {
      if (selectedGroupIds.length === 0) return false;  // No groups = no matches
      const matchesGroup =
        (unit.primary_group_id && selectedGroupIds.includes(unit.primary_group_id)) ||
        (selectedGroupIds.includes(`__unassigned_${unit.company_id}__`) && !unit.primary_group_id);
      if (!matchesGroup) return false;
    }

    // Model filter (NO models selected = NO data shown)
    if (selectedModels !== undefined) {
      if (selectedModels.length === 0) return false;  // No models = no matches
      const modelPrefix = unit.unit_code?.substring(0, 3) || '';
      if (!selectedModels.includes(modelPrefix)) return false;
    }

    // Status filter (using live status if available)
    // Only apply if not all 5 statuses are selected (optimization)
    if (selectedStatuses !== undefined && selectedStatuses.length > 0 && selectedStatuses.length < 5) {
      const status = getLiveStatus
        ? getLiveStatus(unit.id, unit.status)
        : unit.status;
      if (!selectedStatuses.includes(status)) return false;
    }

    return true;
  });
}

// Backward compatibility alias (deprecated)
/** @deprecated Use preFilterUnits instead */
export const preFilterTrucks = preFilterUnits;
